import { ReadonlyVec3, vec3 } from 'gl-matrix';
import {
    dcmjsGetString,
    dcmjsReadRoiObservationsProperty,
    dcmjsReadRoiPhysicalProperty,
    dcmjsReadRoiSequenceProperty,
    dcmjsRemoveStructure,
    dcmjsSetString,
    dcmjsUpdateContourSequence,
    dcmjsWriteContourSequenceProperty,
    dcmjsWriteRoiObservationsProperty,
    dcmjsWriteRoiPhysicalProperty,
    dcmjsWriteRoiSequenceProperty,
} from '../services/dicomProcessor';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { DicomBase } from './dicomBase';
import { Modality } from './modality';
import { getDICOMDate, getDICOMTime } from './utils';

export type Color = { R: number; G: number; B: number };

export enum ContourGeometricType {
    POINT = 'POINT',
    OPEN_PLANAR = 'OPEN_PLANAR',
    OPEN_NONPLANAR = 'OPEN_NONPLANAR',
    CLOSED_PLANAR = 'CLOSED_PLANAR',
}

export enum RoiGenerationAlgorithmType {
    MANUAL = 'MANUAL',
    AUTOMATIC = 'AUTOMATIC',
    SEMI_AUTOMATIC = 'SEMI_AUTOMATIC',
    UNKNOWN = '',
}

export enum RoiInterpretedType {
    CTV = 'CTV', // Clinical Target Volume
    GTV = 'GTV', // Gross Tumor Volume
    PTV = 'PTV', // Planning Target Volume
    EXTERNAL = 'EXTERNAL', // External patient contour, the BODY contour
    BOLUS = 'BOLUS', // patient bolus to be used for external beam therapy
    SUPPORT = 'SUPPORT', // external patient support device
    FIXATION = 'FIXATION', // external patient fixation or immobilization device
    CONTROL = 'CONTROL', // ROI to be used in control of dose optimization and calculation
    ORGAN = 'ORGAN', // patient organ
    OAR = 'OAR', // Organ at Risk
    MARKER = 'MARKER', // patient marker or marker on a localizer
    AVOIDANCE = 'AVOIDANCE', // region in which dose is to be minimized
    TREATED_VOLUME = 'TREATED_VOLUME', // Treated Volume
    IRRAD_VOLUME = 'IRRAD_VOLUME', // Irradiated Volume
    CONTRAST_AGENT = 'CONTRAST_AGENT', // volume into which a contrast agent has been injected
    REGISTRATION = 'REGISTRATION', // registration ROI
    ISOCENTER = 'ISOCENTER', // treatment isocenter to be used for external beam therapy
    CAVITY = 'CAVITY', // patient anatomical cavity
    BRACHY_CHANNEL = 'BRACHY_CHANNEL', // brachytherapy channel
    BRACHY_ACCESSORY = 'BRACHY_ACCESSORY', // brachytherapy accessory device
    BRACHY_SRC_APP = 'BRACHY_SRC_APP', // brachytherapy source applicator
    BRACHY_CHNL_SHLD = 'BRACHY_CHNL_SHLD', // brachytherapy channel shield
    DOSE_REGION = 'DOSE_REGION', // ROI to be used as a dose reference
    DOSE_MEASUREMENT = 'DOSE_MEASUREMENT', // ROI representing a dose measurement device, such as a chamber or TLD
    DEVICE = 'DEVICE', // device not addressed by another Defined Term
    UNKNOWN = '', // Unknown
}

export const ROI_TARGET_TYPES = [
    RoiInterpretedType.CTV,
    RoiInterpretedType.GTV,
    RoiInterpretedType.PTV,
];

export const ROI_OAR_TYPES = [
    RoiInterpretedType.EXTERNAL,
    RoiInterpretedType.ORGAN,
    RoiInterpretedType.OAR,
    RoiInterpretedType.CAVITY,
];

export const AVAILABLE_ROI_TYPES = [...ROI_OAR_TYPES, ...ROI_TARGET_TYPES];

export const ROI_POINT_TYPES = [
    RoiInterpretedType.MARKER,
    RoiInterpretedType.ISOCENTER,
    RoiInterpretedType.REGISTRATION,
];

export type RoiIdentificationCodeSequence = {
    codeValue: string;
    codingSchemeDesignator: string;
    codingSchemeVersion: string;
    codeMeaning: string;
};

export enum DicomVRMaxLength {
    SHORT = 16,
    LONG = 64,
    TEXT = 1024,
}

export enum DICOMApprovalStatus {
    UNAPPROVED = 'UNAPPROVED',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
}

export class DicomContour {
    public constructor(
        public readonly type: ContourGeometricType,
        public readonly data: ReadonlyArray<ReadonlyVec3>,
        public readonly referencedSOPClassUID: string,
        public readonly referencedSOPInstanceUID: string
    ) {}

    public get length() {
        return this.data.length;
    }

    public get isValid(): boolean {
        if (this.length > 2) {
            //This is not ideal, but has to be done
            const a = this.data[0] as vec3;
            const delta = vec3.create();
            const dir = vec3.normalize(
                vec3.create(),
                vec3.subtract(delta, this.data[1] as vec3, a)
            );
            for (let i = 2; i < this.data.length; ++i) {
                vec3.subtract(delta, this.data[i] as vec3, a);
                vec3.normalize(delta, delta);
                const dot = vec3.dot(dir, delta);
                if (1.0 - Math.abs(dot) > 1e-6) {
                    return true;
                }
            }
        }
        return false;
    }
}

export class Roi {
    public readonly roiNumber: number;
    private _name: string;
    private _visible: boolean;
    private _contours: DicomContour[] = [];
    private _color: Color;
    private _contourThickness: number;
    private listeners: RoiListener[] = [];
    private _approvalStatus: DICOMApprovalStatus =
        DICOMApprovalStatus.UNAPPROVED;

    constructor(
        private readonly structureSetDatasetWrapper: DcmJsDatasetWrapper,
        roiNumber: number,
        name: string,
        color: Color = { R: 127, G: 127, B: 127 }
    ) {
        this.roiNumber = roiNumber;
        this._name = name;
        this._color = color;
        this._visible = true;
        this._contourThickness = 1;
    }

    public get contours(): DicomContour[] {
        return this._contours;
    }

    public set contours(contours: DicomContour[]) {
        this._contours = contours;
    }

    public addListener(listener: RoiListener) {
        this.listeners.push(listener);
    }

    public removeListener(listener: RoiListener): boolean {
        const index = this.listeners.indexOf(listener);
        if (index >= 0) {
            this.listeners.splice(index, 1);
            return true;
        }
        return false;
    }

    public get approvalStatus(): DICOMApprovalStatus {
        return this._approvalStatus;
    }

    public get isApproved(): boolean {
        return this.approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    public set approvalStatus(newStatus: DICOMApprovalStatus) {
        if (this._approvalStatus !== newStatus) {
            this._approvalStatus = newStatus;
            this._notifyChange('approvalStatus', newStatus);
        }
    }

    public get color(): Color {
        return this._color;
    }

    public set color(newColor: Color) {
        if (
            this._color.R !== newColor.R ||
            this._color.G !== newColor.G ||
            this._color.B !== newColor.B
        ) {
            this._color = newColor;
            dcmjsWriteContourSequenceProperty(
                this.structureSetDatasetWrapper,
                this.roiNumber,
                'ROIDisplayColor',
                [newColor.R, newColor.G, newColor.B]
            );
            this._notifyChange('color', newColor);
        }
    }

    public get name(): string {
        return this._name;
    }

    public set name(newName: string) {
        this._ensureCanModify();

        if (newName.length > DicomVRMaxLength.LONG) {
            newName = newName.substring(0, DicomVRMaxLength.LONG);
        }

        if (newName !== this._name) {
            this._name = newName;
            dcmjsWriteRoiSequenceProperty(
                this.structureSetDatasetWrapper,
                this.roiNumber,
                'ROIName',
                newName
            );
            this._notifyChange('name', newName);
        }
    }

    public get contourThickness(): number {
        return this._contourThickness;
    }

    public set contourThickness(thickness: number) {
        if (this._contourThickness !== thickness) {
            this._contourThickness = thickness;
            this._notifyChange('contourThickness', thickness);
        }
    }

    public get visible(): boolean {
        return this._visible;
    }

    public set visible(visible: boolean) {
        if (this._visible !== visible) {
            this._visible = visible;
            this._notifyChange('visibility', visible);
        }
    }

    public setVisibilityNoNotification(visible: boolean) {
        this._visible = visible;
    }

    /**
     * RT ROI Interpreted Type Attribute Tag (3006,00A4)
     */
    public get interpretedType(): RoiInterpretedType {
        const val = dcmjsReadRoiObservationsProperty<RoiInterpretedType>(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'RTROIInterpretedType'
        );
        return val ? val : RoiInterpretedType.UNKNOWN;
    }

    public get identificationCode(): RoiIdentificationCodeSequence | undefined {
        const val = dcmjsReadRoiObservationsProperty<
            {
                CodeValue: string;
                CodingSchemeDesignator: string;
                CodingSchemeVersion: string;
                CodeMeaning: string;
            }[]
        >(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'RTROIIdentificationCodeSequence'
        );
        return val && val.length === 1
            ? {
                  codeValue: val[0].CodeValue,
                  codingSchemeDesignator: val[0].CodingSchemeDesignator,
                  codingSchemeVersion: val[0].CodingSchemeVersion,
                  codeMeaning: val[0].CodeMeaning,
              }
            : undefined;
    }

    public set interpretedType(val: RoiInterpretedType) {
        this._ensureCanModify();
        dcmjsWriteRoiObservationsProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'RTROIInterpretedType',
            val
        );
    }

    /**
     * ROI Physical Property Value Attribute (3006,00B4) from ROI Physical Properties Sequence Attribute (3006,00B0).
     * We only support the electron density override value.
     */
    public get relativeElectronDensity(): string | undefined {
        const physicalProperty = dcmjsReadRoiPhysicalProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber
        )?.ROIPhysicalProperty;
        return physicalProperty;
    }

    public get densityOverride(): number | undefined {
        const physicalPropertyValue =
            dcmjsReadRoiPhysicalProperty(
                this.structureSetDatasetWrapper,
                this.roiNumber
            )?.ROIPhysicalPropertyValue;
        
        return physicalPropertyValue;
    }

    public set densityOverride(value: number | undefined) {
        this._ensureCanModify();
        dcmjsWriteRoiPhysicalProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            value
        );
    }

    /**
     * ROI Description Attribute Tag (3006,0028)
     */
    public get roiDescription(): string {
        const val = dcmjsReadRoiSequenceProperty<RoiGenerationAlgorithmType>(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIDescription'
        );
        return val ? val : '';
    }

    public set roiDescription(val: string) {
        if (val.length > DicomVRMaxLength.TEXT) {
            val = val.substring(0, DicomVRMaxLength.TEXT);
        }
        dcmjsWriteRoiSequenceProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIDescription',
            val
        );
    }

    /**
     * ROI Generation Algorithm Attribute Tag (3006,0036)
     */
    public get roiGenerationAlgorithm(): RoiGenerationAlgorithmType {
        const val = dcmjsReadRoiSequenceProperty<RoiGenerationAlgorithmType>(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIGenerationAlgorithm'
        );
        return val ? val : RoiGenerationAlgorithmType.UNKNOWN;
    }

    public set roiGenerationAlgorithm(val: RoiGenerationAlgorithmType) {
        dcmjsWriteRoiSequenceProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIGenerationAlgorithm',
            val
        );
    }

    /**
     * ROI Generation Description Attribute Tag (3006,0038)
     */
    public get roiGenerationDescription(): string {
        const val = dcmjsReadRoiSequenceProperty<string>(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIGenerationDescription'
        );
        return val ? val : '';
    }

    public set roiGenerationDescription(val: string) {
        if (val.length > DicomVRMaxLength.LONG) {
            val = val.substring(0, DicomVRMaxLength.LONG);
        }
        dcmjsWriteRoiSequenceProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIGenerationDescription',
            val
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _notifyChange(property: string, value: any) {
        for (const listener of this.listeners) {
            switch (property) {
                case 'name':
                    listener.onRoiNameChange?.(this, value);
                    break;
                case 'color':
                    listener.onRoiColorChange?.(this, value);
                    break;
                case 'visibility':
                    listener.onRoiVisibilityChange?.(this, value);
                    break;
                case 'approvalStatus':
                    listener.onRoiApprovalStatusChange?.(this, value);
                    break;
                case 'contourThickness':
                    listener.onRoiContourThicknessChange?.(this, value);
                    break;
                default:
                    break;
            }
        }
    }

    private _ensureCanModify() {
        if (this.isApproved) {
            throw new Error(`Cannot modify approved structure '${this._name}'`);
        }
        if (this._isStructureSetApproved()) {
            throw new Error(
                `Cannot modify structure '${this.name}' in approved structure set`
            );
        }
    }

    // helper to read the structure set approval status from the DICOM dataset
    private _isStructureSetApproved(): boolean {
        return (
            dcmjsGetString(
                this.structureSetDatasetWrapper,
                'ApprovalStatus'
            ) === DICOMApprovalStatus.APPROVED
        );
    }
}

export class DicomStructureSet extends DicomBase implements Iterable<Roi> {
    private _structureSetLabel: string;
    private _structureSetDescription: string;
    public frameOfReferenceUid: string;

    /**
     * Map where key is the {@link Roi.roiNumber} and value is the {@link Roi}.
     */
    public rois: Map<number, Roi>;

    /**
     * (0008,1155) Referenced SOP Instance UID Attribute
     * from (3006,0016) Contour Image Sequence Attribute
     * TODO: or alternatively could also get from (3006,0010) Referenced Frame of Reference Sequence Attribute
     */
    public readonly referencedSopUids: Set<string>;

    /**
     * (0008,1150) Referenced SOP Class UID Attribute
     */
    public readonly referencedSopClassUid: string;

    public readonly referencedSeriesUid: string;

    public readonly seriesInstanceUid: string;

    constructor(
        dataset: DcmJsDatasetWrapper,
        sopUid: string,
        frameOfReferenceUid: string,
        referencedSeriesUid: string,
        structureSetLabel: string,
        structureSetDescription: string,
        rois: Map<number, Roi> | undefined = undefined,
        referencedSopUids: string[] = [],
        referencedSopClassUid: string,
        seriesInstanceUid: string,
    ) {
        super(sopUid, Modality.RTSTRUCT, dataset);
        this._structureSetLabel = structureSetLabel;
        this._structureSetDescription = structureSetDescription;
        this.frameOfReferenceUid = frameOfReferenceUid;
        this.referencedSeriesUid = referencedSeriesUid;
        this.rois = rois ?? new Map<number, Roi>();
        this.referencedSopUids = new Set(referencedSopUids);
        this.referencedSopClassUid = referencedSopClassUid;
        this.seriesInstanceUid = seriesInstanceUid;
    }

    public get numberOfStructures(): number {
        return this.rois.size;
    }

    public addRoi(roi: Roi): void {
        this.rois.set(roi.roiNumber, roi);
    }

    public getRoi(roiNumber: number): Roi | undefined {
        return this.rois.get(roiNumber);
    }

    public clearRois() {
        this._ensureCanModify();
        this.rois.clear();
    }

    public deleteRoi(roiNumber: number) {
        this._ensureCanModify();
        const roi = this.rois.get(roiNumber);
        if (roi) {
            dcmjsRemoveStructure(this.datasetWrapper, roiNumber);
            this.rois.delete(roiNumber);
        }
    }

    /**
     * Set the approval status of all ROIs in the structure set.
     */
    public setStructureSetApprovalStatus(
        approvalStatus: DICOMApprovalStatus,
        reviewerName: string
    ): void {
        this.approvalStatus = approvalStatus;
        let reviewDate = '';
        let reviewTime = '';

        if (approvalStatus === DICOMApprovalStatus.APPROVED) {
            this.reviewerName = reviewerName;
            reviewDate = this.reviewDate ? this.reviewDate : getDICOMDate();
            reviewTime = this.reviewTime ? this.reviewTime : getDICOMTime();
        } else {
            this.reviewerName = '';
        }

        this.reviewDate = reviewDate;
        this.reviewTime = reviewTime;
    }

    public setRoisApprovalStatus(newStatus?: DICOMApprovalStatus) {
        for (const roi of this.rois.values()) {
            roi.approvalStatus = newStatus ?? this.approvalStatus;
        }
    }

    public setRoisVisibility(visibility: boolean) {
        for (const roi of this.rois.values()) {
            roi.setVisibilityNoNotification(visibility);
        }
    }

    /**
     * Update the contour sequence with the ROIs contour data.
     */
    public updateContourSequence(roiNumber: number) {
        const roi = this.rois.get(roiNumber);
        if (roi) {
            dcmjsUpdateContourSequence(
                this.datasetWrapper,
                roi.roiNumber,
                roi.contours
            );
        }
    }

    public get structureSetLabel(): string {
        return this._structureSetLabel;
    }

    public set structureSetLabel(newLabel: string) {
        this._ensureCanModify();
        this._structureSetLabel = newLabel;
        dcmjsSetString(this.datasetWrapper, 'StructureSetLabel', newLabel);
    }

    public get structureSetDescription(): string {
        return this._structureSetDescription;
    }

    public set structureSetDescription(newDescription: string) {
        this._ensureCanModify();
        this._structureSetDescription = newDescription;
        dcmjsSetString(
            this.datasetWrapper,
            'StructureSetDescription',
            newDescription
        );
    }

    private _ensureCanModify() {
        if (this.isApproved) {
            throw new Error(
                `Cannot modify approved structure set '${this.structureSetLabel}'`
            );
        }
    }

    public get isApproved(): boolean {
        return this.approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    public get approvalStatus(): DICOMApprovalStatus {
        return dcmjsGetString(
            this.datasetWrapper,
            'ApprovalStatus'
        ) as DICOMApprovalStatus;
    }

    public set approvalStatus(status: DICOMApprovalStatus) {
        dcmjsSetString(this.datasetWrapper, 'ApprovalStatus', status);
    }

    public get reviewerName(): string {
        return dcmjsGetString(this.datasetWrapper, 'ReviewerName');
    }

    public set reviewerName(name: string) {
        dcmjsSetString(this.datasetWrapper, 'ReviewerName', name);
    }

    public get reviewDate(): string {
        return dcmjsGetString(this.datasetWrapper, 'ReviewDate');
    }

    public set reviewDate(date: string) {
        dcmjsSetString(this.datasetWrapper, 'ReviewDate', date);
    }

    public get reviewTime(): string {
        return dcmjsGetString(this.datasetWrapper, 'ReviewTime');
    }

    public set reviewTime(time: string) {
        dcmjsSetString(this.datasetWrapper, 'ReviewTime', time);
    }

    public get manufacturer(): string {
        return dcmjsGetString(this.datasetWrapper, 'Manufacturer');
    }

    public get manufacturerModelName(): string {
        return dcmjsGetString(this.datasetWrapper, 'ManufacturerModelName');
    }

    public get softwareVersions(): string {
        return dcmjsGetString(this.datasetWrapper, 'SoftwareVersions');
    }

    [Symbol.iterator](): Iterator<Roi, Roi, undefined> {
        return this.rois.values() as Iterator<Roi, Roi, undefined>;
    }
}

export interface RoiListener {
    onRoiVisibilityChange?(roi: Roi, visible: boolean): void;

    onRoiColorChange?(roi: Roi, color: Color): void;

    onRoiContourThicknessChange?(roi: Roi, contourThickness: number): void;

    onRoiNameChange?(roi: Roi, name: string): void;

    onRoiApprovalStatusChange?(roi: Roi, status: DICOMApprovalStatus): void;
}
